import { useEffect, useRef } from "react";
import type { LipSyncMorphTarget } from "./useLipSync";

// Liveness Group A — applies a subtle baseline smile when the avatar is
// idle/listening. Gated by `isSpeaking` so the smile decays to neutral
// while useLipSync drives mouth morphs (no race on mouthSmileLeft/Right
// influences during speech).

export interface UseFaceBaselineOptions {
  morphTarget: LipSyncMorphTarget | null;
  isSpeaking: boolean;
  smileWeight?: number;
  smoothingAlpha?: number;
  enabled?: boolean;
}

const SMILE_MORPHS = ["mouthSmileLeft", "mouthSmileRight"] as const;

export function useFaceBaseline({
  morphTarget,
  isSpeaking,
  smileWeight = 0.12,
  smoothingAlpha = 0.05,
  enabled = true,
}: UseFaceBaselineOptions): void {
  const rafRef = useRef<number | null>(null);
  const currentRef = useRef<number>(0);

  useEffect(() => {
    if (!enabled || !morphTarget) return;

    const tick = () => {
      const target = isSpeaking ? 0 : smileWeight;
      currentRef.current += (target - currentRef.current) * smoothingAlpha;

      for (const morph of SMILE_MORPHS) {
        const idx = morphTarget.morphTargetDictionary[morph];
        if (idx === undefined) continue;
        morphTarget.morphTargetInfluences[idx] = currentRef.current;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      for (const morph of SMILE_MORPHS) {
        const idx = morphTarget.morphTargetDictionary[morph];
        if (idx === undefined) continue;
        morphTarget.morphTargetInfluences[idx] = 0;
      }
      currentRef.current = 0;
    };
  }, [morphTarget, isSpeaking, smileWeight, smoothingAlpha, enabled]);
}
